import { Card, CardContent, CardHeader } from "../../primitives/card";
import { Badge } from "../../primitives/badge";
import { MessageSquare, Bell } from "lucide-react";
import type * as API from "@shared-core/data/api-types";
import { formatDistanceToNow } from "date-fns";
import {
  formatDateOrDash,
  formatDateTimeOrDash,
} from "@shared-core/domain/formatters";

interface NoteCardProps {
  note: API.Note | API.NoteInTicketView;
  compact?: boolean;
}

export function NoteCard({ note, compact = false }: NoteCardProps) {
  const createdDate = new Date(note.created_at);
  const timeAgo = formatDistanceToNow(createdDate, { addSuffix: true });

  if (compact) {
    return (
      <div className="flex flex-col sm:flex-row sm:items-start gap-3 p-4 border border-gray-200 rounded-lg bg-card shadow-sm">
        <MessageSquare className="size-5 text-muted-foreground shrink-0 mt-0.5 sm:block hidden" />
        <div className="flex items-start gap-3 flex-1">
          <MessageSquare className="size-5 text-muted-foreground shrink-0 mt-0.5 sm:hidden" />
          <div className="flex-1 min-w-0">
            <p className="text-sm mb-2">{note.note_text}</p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>{timeAgo}</span>
              {note.remind_me_at && (
                <Badge variant="outline" className="text-xs">
                  <Bell className="size-3 mr-1" />
                  Reminder set
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start gap-4">
          <MessageSquare className="size-5 text-muted-foreground shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                {note.note_title && (
                  <Badge variant="outline">{note.note_title}</Badge>
                )}
                {note.remind_me_at && (
                  <Badge variant="outline">
                    <Bell className="size-3 mr-1" />
                    Reminder
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm mb-4">{note.note_text}</p>
        <div className="flex items-center justify-between text-sm">
          <div className="text-muted-foreground">
            Created {timeAgo} • {formatDateTimeOrDash(createdDate)}
          </div>
          {note.remind_me_at && (
            <div className="text-muted-foreground">
              Reminder: {formatDateOrDash(note.remind_me_at)}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
